"use client";

import { useEffect, useState, useRef } from "react";
import { motion, useInView } from "framer-motion";

interface DecryptedTextProps {
  text: string;
  speed?: number;
  maxIterations?: number;
  sequential?: boolean;
  revealDirection?: "start" | "end" | "center";
  useOriginalCharsOnly?: boolean;
  characters?: string;
  className?: string;
  parentClassName?: string;
  encryptedClassName?: string;
  animateOn?: "view" | "hover";
}

export default function DecryptedText({
  text,
  speed = 50,
  maxIterations = 10,
  sequential = false,
  revealDirection = "start",
  useOriginalCharsOnly = false,
  characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890!@#$%^&*()_+",
  className = "",
  parentClassName = "",
  encryptedClassName = "",
  animateOn = "hover",
}: DecryptedTextProps) {
  const [displayText, setDisplayText] = useState(text);
  const [isHovering, setIsHovering] = useState(false);
  const [isScrambling, setIsScrambling] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  // Check if element is in view (only if animateOn="view")
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  const scramble = () => {
    if (isScrambling) return;
    setIsScrambling(true);

    const originalText = text.split("");
    let iteration = 0;

    const intervalId = setInterval(() => {
      let scrambled = "";

      // Logic for sequential reveal vs all-at-once
      if (sequential) {
        scrambled = originalText
          .map((char, index) => {
            // If the character is a space, keep it a space
            if (char === " ") return " ";
            // If we've passed enough iterations for this index, show real char
            if (index < Math.floor(iteration / maxIterations)) {
              return originalText[index];
            }
            return characters[Math.floor(Math.random() * characters.length)];
          })
          .join("");
      } else {
        // All at once scrambling
        scrambled = originalText
          .map((char, index) => {
            if (char === " ") return " ";
            if (iteration >= maxIterations) {
              return originalText[index];
            }
            return characters[Math.floor(Math.random() * characters.length)];
          })
          .join("");
      }

      setDisplayText(scrambled);
      iteration++;

      if (sequential) {
        if (iteration > originalText.length * maxIterations) {
          clearInterval(intervalId);
          setDisplayText(text);
          setIsScrambling(false);
        }
      } else {
        if (iteration > maxIterations) {
          clearInterval(intervalId);
          setDisplayText(text);
          setIsScrambling(false);
        }
      }
    }, speed);
  };

  // Trigger for "view" mode
  useEffect(() => {
    if (animateOn === "view" && isInView) {
      scramble();
    }
  }, [isInView, animateOn]);

  // Trigger for "hover" mode
  const handleMouseEnter = () => {
    if (animateOn === "hover") {
      setIsHovering(true);
      scramble();
    }
  };

  const handleMouseLeave = () => {
    if (animateOn === "hover") {
      setIsHovering(false);
    }
  };

  return (
    <span
      ref={containerRef}
      className={`inline-block whitespace-pre-wrap ${parentClassName}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className={className}>{displayText}</span>
    </span>
  );
}
